import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '../api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [permissions, setPermissions] = useState([]);

  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    if (user.role === 'superadmin' || user.role === 'admin') return true;
    if (user.role === 'staff') return permissions.includes(permission);
    return false;
  }, [user, permissions]);

  const canAccess = useCallback((feature) => {
    if (!user) return false;
    if (user.role === 'superadmin') return true;
    if (user.role === 'admin') {
      const superAdminOnlyFeatures = ['all_users', 'all_pharmacies', 'system_settings'];
      return !superAdminOnlyFeatures.includes(feature);
    }
    if (user.role === 'staff') return hasPermission(feature);
    return false;
  }, [user, hasPermission]);

  // ✅ UPDATED: Ensures expired users get the "Upgrade" banner
  const needsUpgrade = useCallback(() => {
    if (!user) return false;
    if (user.role === 'superadmin') return false;
    if (user.role === 'staff') return false; 
    
    // Explicitly check for expired status
    if (user.status === 'expired') return true;
    
    if (user.status === 'trial' && user.trial) {
      return user.trial.isExpired === true;
    }
    
    return ['blocked', 'inactive'].includes(user.status);
  }, [user]);

  const hasFullAccess = useCallback(() => {
    if (!user) return false;
    if (user.role === 'superadmin') return true;
    if (user.role === 'admin' && user.status === 'active') return true;
    if (user.role === 'staff' && user.isActive) return true;
    if (user.status === 'trial' && user.trial && !user.trial.isExpired) return true;
    return false;
  }, [user]);

  const getDaysRemaining = useCallback(() => {
    if (!user) return 0;
    if (user.role === 'superadmin') return null;
    if (user.role === 'staff') return null;
    if (user.status === 'active' && subscription?.endDate) {
      const end = new Date(subscription.endDate);
      const now = new Date();
      return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
    }
    if (user.status === 'trial' && user.trial?.daysRemaining !== undefined) return user.trial.daysRemaining;
    return 0;
  }, [user, subscription]);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); setUser(null); setPermissions([]); return null; }

    try {
      const response = await API.get('/users/profile');
      let userData = response.data;
      
      if (userData.permissions) setPermissions(userData.permissions);
      else if (userData.role === 'superadmin' || userData.role === 'admin') {
        setPermissions(['dashboard', 'parties', 'transactions', 'medicines', 'billing', 'sales', 'expiry', 'contact', 'profile', 'reports', 'staff_management']);
      }
      
      if (userData.role === 'superadmin') {
        userData.status = 'active';
        userData.trial = { isTrialUser: false, isExpired: false };
      } 
      else if (userData.role === 'admin') {
        try {
          const trialResponse = await API.get('/users/trial-status');
          if (trialResponse.data) {
            userData.trial = trialResponse.data.trial;
            userData.status = trialResponse.data.status || userData.status;
          }
        } catch (e) {}
        
        try {
          const subResponse = await API.get('/subscriptions/my-subscription');
          if (subResponse.data.currentSubscription) setSubscription(subResponse.data.currentSubscription);
          if (subResponse.data.trial) userData.trial = subResponse.data.trial;
          if (subResponse.data.userStatus) userData.status = subResponse.data.userStatus;
        } catch (e) {}
      }
      
      setUser(userData);
      localStorage.setItem('userStatus', userData.status);
      localStorage.setItem('userRole', userData.role);
      
      return userData;
    } catch (error) {
      console.error('Auth fetch error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('userStatus');
        localStorage.removeItem('userRole');
        setUser(null);
        setPermissions([]);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (userData, token, trialData = null) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userRole', userData.role);
    localStorage.setItem('userName', userData.name);
    localStorage.setItem('pharmacyName', userData.pharmacyName);
    
    if (userData.role === 'superadmin') {
        userData.status = 'active';
        userData.trial = { isTrialUser: false };
        localStorage.setItem('userStatus', 'active');
    } else {
        localStorage.setItem('userStatus', userData.status || 'active');
    }
    
    if (trialData && userData.role !== 'superadmin') {
      localStorage.setItem('trialInfo', JSON.stringify(trialData));
      userData.trial = trialData;
    }
    
    if (userData.permissions) setPermissions(userData.permissions);
    else if (userData.role === 'superadmin' || userData.role === 'admin') {
      setPermissions(['dashboard', 'parties', 'transactions', 'medicines', 'billing', 'sales', 'expiry', 'contact', 'profile', 'reports', 'staff_management']);
    }
    
    setUser(userData);
    setLoading(false);
    return userData;
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      try {
        const statusResponse = await API.get('/payments/user-status');
        if (statusResponse.data.success) {
          const { user: userData, activeSubscription } = statusResponse.data;
          if (userData.role === 'superadmin') userData.status = 'active';
          setUser(userData);
          setSubscription(activeSubscription);
          localStorage.setItem('userStatus', userData.status);
          return { user: userData, subscription: activeSubscription };
        }
      } catch (e) {}
      return await fetchUser();
    } catch (error) {
      return null;
    }
  }, [fetchUser]);

  const updateUser = useCallback((newUserData) => {
    setUser(prev => {
      const updated = { ...prev, ...newUserData };
      if (updated.role === 'superadmin') updated.status = 'active';
      localStorage.setItem('userStatus', updated.status);
      return updated;
    });
    if (newUserData.permissions) setPermissions(newUserData.permissions);
    if (newUserData.subscription) setSubscription(prev => ({ ...prev, ...newUserData.subscription }));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('pendingPayment');
    localStorage.removeItem('userStatus');
    localStorage.removeItem('trialInfo');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    localStorage.removeItem('pharmacyName');
    setUser(null);
    setSubscription(null);
    setPermissions([]);
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const value = {
    user, subscription, loading, permissions, login, logout, refreshUser, updateUser, fetchUser, needsUpgrade, hasFullAccess, getDaysRemaining, hasPermission, canAccess,
    isAuthenticated: !!user, isSuperAdmin: user?.role === 'superadmin', isAdmin: user?.role === 'admin' || user?.role === 'superadmin', isStaff: user?.role === 'staff'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;